/**
 * Client half of the advisory single-editor lease (MDRS-554 / audit RB-8).
 *
 * The version check is what guarantees no work is lost. This is what stops
 * users reaching it: the second tab opens read-only with a banner naming who
 * holds the report, rather than letting someone type for ten minutes and then
 * telling them it can't be saved.
 *
 * Advisory on purpose. Every branch here that lets editing through is load-
 * bearing — a lease that could strand a doctor outside their own report
 * (holder's laptop closed, tab crashed, lease unreachable) would be a worse
 * bug than the overwrite it replaces. When in doubt, this lets the user edit
 * and leaves the version check to catch the collision.
 */

/** Identifies THIS tab. The ticket is "two tabs", one account — a user id
 *  can't tell them apart, so the lease needs a per-tab token. */
const SESSION_KEY = "rb.editorSessionId";

export interface EditorLeaseState {
  /** This exact tab holds the lease. */
  heldByMe: boolean;
  /** The holder is the SAME user in a different tab — the ticket's own
   *  scenario, and the one case where naming the holder reads as a bug
   *  ("Ray Saavedra is editing this report", shown to Ray). */
  holderIsSelf: boolean;
  holderUserId: string | null;
  holderName: string | null;
  expiresAt: string | null;
}

/**
 * A stable id for this browser tab.
 *
 * `sessionStorage` is per-tab by definition and survives a refresh, which is
 * exactly the lifetime a lease should have: a reload keeps editing, a new tab
 * is a new editor. Falls back to a non-persisted id where storage is
 * unavailable (private mode, blocked storage) — the lease then churns on
 * refresh, which is a degraded banner rather than a broken editor.
 */
let memoisedSessionId: string | null = null;

/** Monotonic suffix for the no-Web-Crypto path below. Module-scoped, so two
 *  ids minted in the same millisecond inside one tab still differ. */
let sessionTokenSequence = 0;

/**
 * An unpredictable token for this tab.
 *
 * Entropy comes from Web Crypto, never `Math.random` — CodeQL flags that as
 * insecure randomness (high), and rightly: this value names a session and keys
 * a lock, and "the lock is only advisory" is not a reason to seed an
 * identifier from a predictable PRNG. A guessable id lets another authorised
 * viewer impersonate a tab's heartbeat, which would quietly reduce the lease
 * to nothing — both tabs believing they hold it — rather than surfacing a
 * banner.
 *
 * `randomUUID` needs a secure context; `getRandomValues` does not, so the
 * fallback stays inside Web Crypto rather than dropping to a weak PRNG the
 * moment the page isn't served over HTTPS. The last branch is for an
 * environment with no Web Crypto at all (there is none we ship to) and is
 * deliberately NOT random — a predictable-but-honest id is better than
 * reaching for `Math.random` and calling it entropy. It still can't break the
 * editor, which is the property that actually matters here.
 */
const randomSessionToken = (): string => {
  const webCrypto = globalThis.crypto;
  try {
    if (typeof webCrypto?.randomUUID === "function") {
      return webCrypto.randomUUID();
    }
  } catch {
    // Some engines expose `randomUUID` but throw outside a secure context.
  }
  if (typeof webCrypto?.getRandomValues === "function") {
    const bytes = webCrypto.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  }
  sessionTokenSequence += 1;
  return `t${Date.now().toString(36)}${sessionTokenSequence.toString(36)}`;
};

export const editorSessionId = (): string => {
  if (memoisedSessionId) return memoisedSessionId;
  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored) {
      memoisedSessionId = stored;
      // The branch that actually matters for duplicate tabs: a copy READS an
      // inherited id rather than minting one, so this is where the collision
      // arrives.
      startDuplicateTabProbe(stored);
      return stored;
    }
  } catch {
    // Storage blocked (private mode, hardened browser). Fall through to a
    // memory-only id: the lease then churns on refresh, which degrades the
    // banner rather than taking the editor down for an advisory feature.
  }
  const fresh = randomSessionToken();
  try {
    sessionStorage.setItem(SESSION_KEY, fresh);
  } catch {
    // Same as above — a non-persisted id is still a usable id.
  }
  memoisedSessionId = fresh;
  startDuplicateTabProbe(fresh);
  return fresh;
};

/** Test seam: drop the in-memory cache so a cleared sessionStorage reads as a
 *  new tab. Production code has no reason to call this — a tab that changed
 *  its own id mid-session would contend with its own previous heartbeat. */
export const __resetEditorSessionId = () => {
  memoisedSessionId = null;
};

// --- duplicate-tab detection ----------------------------------------------
//
// Browsers COPY `sessionStorage` into the new tab on "Duplicate tab", and on
// `window.open` / `target="_blank"` with an opener. Both tabs then read the
// same `rb.editorSessionId`, the server sees one editor renewing its own
// lease, and NEITHER tab shows a banner — in the exact scenario this ticket is
// named after. The version check still blocks the overwrite, so the cost is
// the conflict dialog the lease exists to prevent.
//
// So a booting tab announces its id, and any live tab already holding that id
// answers "taken"; the newcomer then re-mints. If two tabs boot at the same
// instant they answer each other and both re-mint — wasteful but correct,
// since both end up unique.

const CHANNEL_NAME = "rb-editor-session";

/** Identifies this PAGE LOAD. Never persisted, so a duplicated tab cannot
 *  inherit it — which is what lets a tab tell its own traffic apart from a
 *  copy's when they share a session id. */
const pageLoadNonce = randomSessionToken();

type SessionProbe = { type: string; id: string; from: string };

/**
 * What a tab should do about a message on the session channel.
 *
 * Pure, because the interesting part is the decision table rather than the
 * plumbing: answering your own probe would re-mint an id nobody else is using,
 * and acting on another id's traffic would disturb an unrelated report.
 */
export const classifySessionProbe = (
  message: unknown,
  selfId: string,
  selfNonce: string
): "answer" | "remint" | "ignore" => {
  if (!message || typeof message !== "object") return "ignore";
  const { type, id, from } = message as Partial<SessionProbe>;
  if (typeof type !== "string" || typeof id !== "string") return "ignore";
  if (typeof from !== "string" || from === selfNonce) return "ignore";
  if (id !== selfId) return "ignore";
  if (type === "probe") return "answer";
  if (type === "taken") return "remint";
  return "ignore";
};

let channel: BroadcastChannel | null = null;

/** Announce this tab's id and re-mint if a live tab already holds it.
 *  No-ops where BroadcastChannel is unavailable — the lease is advisory and
 *  must never take the editor down. */
const startDuplicateTabProbe = (id: string): void => {
  if (channel || typeof BroadcastChannel === "undefined") return;
  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
  } catch {
    return;
  }
  channel.onmessage = (event: MessageEvent) => {
    const verdict = classifySessionProbe(
      event.data,
      memoisedSessionId ?? "",
      pageLoadNonce
    );
    if (verdict === "answer") {
      channel?.postMessage({
        type: "taken",
        id: memoisedSessionId,
        from: pageLoadNonce,
      });
    } else if (verdict === "remint") {
      const fresh = randomSessionToken();
      try {
        sessionStorage.setItem(SESSION_KEY, fresh);
      } catch {
        // Memory-only is still a usable id.
      }
      memoisedSessionId = fresh;
    }
  };
  channel.postMessage({ type: "probe", id, from: pageLoadNonce });
};

/**
 * Whether this tab may edit, given the lease and the case's lock state.
 *
 * Read-only only when someone else DEMONSTRABLY holds a live lease. Unknown
 * lease, failed request, expired holder — all resolve to editable.
 */
export const canEditWithLease = (
  lease: EditorLeaseState | null | undefined,
  opts: { isFinalised: boolean; isReloading?: boolean; hasConflict?: boolean }
): boolean => {
  // MDRS-271 already froze the case server-side; the lease must not read as
  // permission to type into a finalised report.
  if (opts.isFinalised) return false;
  // Nothing typed now can survive. A reload is about to swap the document out
  // from under the caret and clear the edited flag; an unresolved conflict has
  // already stopped autosave. The conflict dialog is modal so it guards its
  // own reload, but the takeover path has no dialog — it claims the lease,
  // goes editable, and only then awaits the refetch.
  if (opts.isReloading || opts.hasConflict) return false;
  if (!lease) return true;
  if (lease.heldByMe) return true;
  // Nobody demonstrably holds it — an expired lease arrives with a null
  // holder, and so does a lease we failed to read. Both mean "edit".
  return !lease.holderUserId;
};

/**
 * The sentence shown in the read-only banner.
 *
 * Returns null when there is nothing to say, so the banner is absent rather
 * than empty.
 */
export const leaseBannerMessage = (
  lease: EditorLeaseState | null | undefined
): string | null => {
  if (!lease || lease.heldByMe || !lease.holderUserId) return null;
  // Says "the report body", not "editing". `canEdit` is consumed in exactly
  // one place — the <DocxEditor> `readOnly` prop — so the module panel on the
  // left stays live and still autosaves. Claiming that editing is paused when
  // it demonstrably isn't would be worse than saying nothing: the user would
  // trust a guarantee the UI isn't making, and a save they didn't know they
  // were making would push the HOLDER into a conflict. Widening `canEdit` to
  // the module panel is the better fix; until then the copy tells the truth.
  if (lease.holderIsSelf) {
    // The ticket's own scenario. "Ray Saavedra is editing this report", shown
    // to Ray, reads as a bug rather than as an explanation.
    return "You have this report open in another tab. The report body is read-only here so the two tabs can't overwrite each other.";
  }
  const who = lease.holderName?.trim() || "Another user";
  return `${who} is editing this report. The report body is read-only here so your changes can't overwrite theirs.`;
};
